'use client';

import React from 'react';
import type { Hotel } from '@/components/packages/Hotels';
import type { TransferDisplayItem } from './ItineraryInfoPopover';
import { typography } from '@/design-system/tokens/typography';
import {
  formatRoomOptionsLabel,
  NormalizedRoomOption,
} from '@/utils/roomOptionHelpers';

export interface ItineraryTableDayRow {
  _id: string;
  day: number;
  highlights: Array<{ _id: string; title?: string }>;
  hotelsForDay: Hotel[];
  transfersForDay: TransferDisplayItem[];
  /** When set, accommodation comes from itinerary item (show this instead of hotelsForDay) */
  hotelFromItinerary?: { name?: string; address?: string; location?: string };
  roomFromItinerary?: { name?: string };
  roomOptionsFromItinerary?: NormalizedRoomOption[];
  /** When set, transfer comes from itinerary item (show pickup/dropoff/duration) */
  transferFromItinerary?: {
    pickUpPoint: string;
    dropOffPoint: string;
    duration?: string;
  };
}

interface ItineraryTableViewProps {
  dayInfo: ItineraryTableDayRow[];
  startDate: Date | null;
  hasAccommodation: boolean;
  hasTransfer: boolean;
}

function getRoomDisplayLabel(day: ItineraryTableDayRow): string {
  if (day.roomOptionsFromItinerary && day.roomOptionsFromItinerary.length > 0) {
    return formatRoomOptionsLabel(day.roomOptionsFromItinerary);
  }
  return day.roomFromItinerary?.name?.trim() || '';
}

function formatDayDate(startDate: Date, dayIndex: number): string {
  const day = new Date(startDate);
  day.setDate(day.getDate() + dayIndex);
  return day.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Format duration value as hours (e.g. "2" → "2 hours", "1" → "1 hour") */
function formatDurationHours(duration: string | undefined): string {
  if (duration == null || String(duration).trim() === '') return '';
  const num = parseFloat(String(duration).trim());
  if (Number.isNaN(num)) return duration;
  return num === 1 ? '1 hour' : `${num} hours`;
}

/** Single transfer block for reuse in table and cards */
function TransferContent({
  transfers,
  transferFromItinerary,
}: {
  transfers: TransferDisplayItem[];
  transferFromItinerary?: {
    pickUpPoint: string;
    dropOffPoint: string;
    duration?: string;
  };
}) {
  if (transferFromItinerary) {
    return (
      <div className='space-y-3'>
        <div>
          {transferFromItinerary.pickUpPoint && (
            <p className='text-sm text-black/70'>
              Pickup:{' '}
              <span className='font-medium text-black'>
                {transferFromItinerary.pickUpPoint}
              </span>
            </p>
          )}
          {transferFromItinerary.dropOffPoint && (
            <p className='text-sm text-black/70'>
              Dropoff:{' '}
              <span className='font-medium text-black'>
                {transferFromItinerary.dropOffPoint}
              </span>
            </p>
          )}
          {transferFromItinerary.duration && (
            <p className='text-sm text-black/70'>
              Duration: {formatDurationHours(transferFromItinerary.duration)}
            </p>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className='space-y-3'>
      {transfers.map((t: TransferDisplayItem, i: number) => (
        <div key={i}>
          {t.fromName != null && (
            <p className='text-sm text-black/70'>
              Pickup:{' '}
              <span className='font-medium text-black'>{t.fromName}</span>
            </p>
          )}
          {t.toName != null && (
            <p className='text-sm text-black/70'>
              Dropoff:{' '}
              <span className='font-medium text-black'>{t.toName}</span>
            </p>
          )}
          {t.duration && (
            <p className='text-sm text-black/70'>
              Duration: {formatDurationHours(t.duration)}
            </p>
          )}
          {t.vehicle && (
            <div className='mt-2'>
              <p className='text-xs font-semibold uppercase tracking-wide text-black/60 mb-1.5'>
                Vehicle Details
              </p>
              <p className='font-medium text-black'>{t.vehicle.title}</p>
              {t.vehicle.type && (
                <p className='text-sm text-black/70'>Type: {t.vehicle.type}</p>
              )}
              {typeof t.vehicle.seatingCapacity === 'number' && (
                <p className='text-sm text-black/70'>
                  Seating: {t.vehicle.seatingCapacity} passengers
                </p>
              )}
              {t.vehicle.features?.length > 0 && (
                <p className='text-sm text-black/70 mt-1'>
                  {t.vehicle.features.join(' • ')}
                </p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** Mobile card for one day – used on small screens */
function DayCard({
  day,
  dayIndex,
  startDate,
  hasAccommodation,
  hasTransfer,
  hasStartDate,
}: {
  day: ItineraryTableDayRow;
  dayIndex: number;
  startDate: Date | null;
  hasAccommodation: boolean;
  hasTransfer: boolean;
  hasStartDate: boolean;
}) {
  const {
    hotelsForDay,
    transfersForDay,
    hotelFromItinerary,
    transferFromItinerary,
    day: dayNum,
    highlights = [],
    _id,
  } = day;
  const roomDisplayLabel = getRoomDisplayLabel(day);

  return (
    <article
      key={_id}
      className='border border-[#D9D9D9] rounded-lg overflow-hidden bg-white first:rounded-t-lg last:rounded-b-lg'
    >
      <div className='bg-[#F5F5F5] px-4 py-3 border-b border-[#D9D9D9]'>
        <span className='font-semibold text-black'>Day {dayNum}</span>
        {hasStartDate && startDate && (
          <span className='text-black/80 ml-2'>
            {formatDayDate(startDate, dayIndex)}
          </span>
        )}
      </div>
      <div className='p-4 space-y-4' style={{ fontSize: typography.size.sm }}>
        <div>
          <p className='text-xs font-semibold uppercase tracking-wide text-black/60 mb-1.5'>
            Highlights
          </p>
          {Array.isArray(highlights) && highlights.length > 0 ? (
            <ul className='list-disc pl-5 space-y-1 text-black/80'>
              {highlights.map(h => (
                <li key={h._id}>{h.title ?? ''}</li>
              ))}
            </ul>
          ) : (
            <span className='text-black/50'>—</span>
          )}
        </div>
        {hasAccommodation && (
          <div>
            <p className='text-xs font-semibold uppercase tracking-wide text-black/60 mb-1.5'>
              Accommodation
            </p>
            {hotelFromItinerary || roomDisplayLabel ? (
              <div className='space-y-1'>
                {hotelFromItinerary?.name && (
                  <span className='font-medium text-black'>
                    {hotelFromItinerary.name}
                  </span>
                )}
                {(hotelFromItinerary?.address ||
                  hotelFromItinerary?.location) && (
                  <p className='text-black/70 text-sm mt-0.5'>
                    {hotelFromItinerary?.location ||
                      hotelFromItinerary?.address}
                  </p>
                )}
                {roomDisplayLabel && (
                  <p className='text-black/70 text-sm mt-0.5'>
                    Room: {roomDisplayLabel}
                  </p>
                )}
              </div>
            ) : hotelsForDay && hotelsForDay.length > 0 ? (
              <div className='space-y-1'>
                {hotelsForDay.map((h: Hotel) => (
                  <div key={(h as Hotel & { _id?: string })._id ?? h.name}>
                    <span className='font-medium text-black'>{h.name}</span>
                    {(h.location || h.address) && (
                      <p className='text-black/70 text-sm mt-0.5'>
                        {h.location || h.address}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <span className='text-black/50'>—</span>
            )}
          </div>
        )}
        {hasTransfer && (
          <div>
            <p className='text-xs font-semibold uppercase tracking-wide text-black/60 mb-1.5'>
              Transfer
            </p>
            {transfersForDay && transfersForDay.length > 0 ? (
              <TransferContent transfers={transfersForDay} />
            ) : transferFromItinerary ? (
              <TransferContent
                transfers={[]}
                transferFromItinerary={transferFromItinerary}
              />
            ) : (
              <span className='text-black/50'>—</span>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

const ItineraryTableView: React.FC<ItineraryTableViewProps> = ({
  dayInfo,
  startDate,
  hasAccommodation,
  hasTransfer,
}) => {
  const hasStartDate = startDate !== null && !Number.isNaN(startDate.getTime());

  // Equal width for the last 1–3 columns (Highlights, Accommodation, Transfer); Day & Date use 11% each
  const lastThreeCount = 1 + (hasAccommodation ? 1 : 0) + (hasTransfer ? 1 : 0);
  const lastThreeColWidth = `${(100 - 22) / lastThreeCount}%`;

  return (
    <>
      {/* Mobile: card layout */}
      <div className='md:hidden flex flex-col gap-3'>
        {dayInfo.map((day, dayIndex) => (
          <DayCard
            key={day._id}
            day={day}
            dayIndex={dayIndex}
            startDate={startDate}
            hasAccommodation={hasAccommodation}
            hasTransfer={hasTransfer}
            hasStartDate={hasStartDate}
          />
        ))}
      </div>

      {/* Desktop: scrollable table */}
      <div className='hidden md:block w-full min-w-0 overflow-x-auto border border-[#D9D9D9] rounded-lg'>
        <table
          className='w-full min-w-[600px] border-collapse table-fixed'
          style={{ fontSize: typography.size.sm }}
        >
          <colgroup>
            <col style={{ width: '11%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: lastThreeColWidth }} />
            {hasAccommodation && <col style={{ width: lastThreeColWidth }} />}
            {hasTransfer && <col style={{ width: lastThreeColWidth }} />}
          </colgroup>
          <thead>
            <tr className='bg-[#F5F5F5]'>
              <th className='text-left py-3 px-4 font-semibold text-black border-b border-r border-[#D9D9D9] whitespace-nowrap'>
                Day
              </th>
              <th className='text-left py-3 px-4 font-semibold text-black border-b border-r border-[#D9D9D9] whitespace-nowrap'>
                Date
              </th>
              <th className='text-left py-3 px-4 font-semibold text-black border-b border-[#D9D9D9]'>
                Highlights
              </th>
              {hasAccommodation && (
                <th className='text-left py-3 px-4 font-semibold text-black border-b border-l border-[#D9D9D9]'>
                  Accommodation
                </th>
              )}
              {hasTransfer && (
                <th className='text-left py-3 px-4 font-semibold text-black border-b border-l border-[#D9D9D9]'>
                  Transfer
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {dayInfo.map((day, dayIndex) => {
              const {
                hotelsForDay,
                transfersForDay,
                hotelFromItinerary,
                transferFromItinerary,
                day: dayNum,
                highlights = [],
                _id,
              } = day;
              const roomDisplayLabel = getRoomDisplayLabel(day);
              const hasAccommodationContent =
                (hotelFromItinerary ?? roomDisplayLabel) ||
                (hotelsForDay && hotelsForDay.length > 0);
              const hasTransferContent =
                (transfersForDay && transfersForDay.length > 0) ||
                transferFromItinerary;
              return (
                <tr key={_id} className='align-top hover:bg-[#FAFAFA]'>
                  <td className='py-4 px-4 border-b border-r border-[#E5E5E5] font-semibold text-black'>
                    {dayNum}
                  </td>
                  <td className='py-4 px-4 border-b border-r border-[#E5E5E5] text-black/80 whitespace-nowrap'>
                    {hasStartDate ? formatDayDate(startDate, dayIndex) : '—'}
                  </td>
                  <td className='py-4 px-4 border-b border-[#E5E5E5]'>
                    {Array.isArray(highlights) && highlights.length > 0 ? (
                      <ul className='list-disc pl-5 space-y-1 text-black/80'>
                        {highlights.map(h => (
                          <li key={h._id}>{h.title ?? ''}</li>
                        ))}
                      </ul>
                    ) : (
                      <span className='text-black/50'>—</span>
                    )}
                  </td>
                  {hasAccommodation && (
                    <td className='py-4 px-4 border-b border-l border-[#E5E5E5]'>
                      {hasAccommodationContent ? (
                        <div className='space-y-1'>
                          {(hotelFromItinerary || roomDisplayLabel) && (
                            <>
                              {hotelFromItinerary?.name && (
                                <span className='font-medium text-black'>
                                  {hotelFromItinerary.name}
                                </span>
                              )}
                              {(hotelFromItinerary?.address ||
                                hotelFromItinerary?.location) && (
                                <p className='text-black/70 text-sm mt-0.5'>
                                  {hotelFromItinerary?.location ||
                                    hotelFromItinerary?.address}
                                </p>
                              )}
                              {roomDisplayLabel && (
                                <p className='text-black/70 text-sm mt-0.5'>
                                  Room: {roomDisplayLabel}
                                </p>
                              )}
                            </>
                          )}
                          {!hotelFromItinerary &&
                            !roomDisplayLabel &&
                            hotelsForDay?.map((h: Hotel) => (
                              <div
                                key={
                                  (h as Hotel & { _id?: string })._id ?? h.name
                                }
                              >
                                <span className='font-medium text-black'>
                                  {h.name}
                                </span>
                                {(h.location || h.address) && (
                                  <p className='text-black/70 text-sm mt-0.5'>
                                    {h.location || h.address}
                                  </p>
                                )}
                              </div>
                            ))}
                        </div>
                      ) : (
                        <span className='text-black/50'>—</span>
                      )}
                    </td>
                  )}
                  {hasTransfer && (
                    <td className='py-4 px-4 border-b border-l border-[#E5E5E5]'>
                      {hasTransferContent ? (
                        transfersForDay && transfersForDay.length > 0 ? (
                          <TransferContent transfers={transfersForDay} />
                        ) : transferFromItinerary ? (
                          <TransferContent
                            transfers={[]}
                            transferFromItinerary={transferFromItinerary}
                          />
                        ) : (
                          <span className='text-black/50'>—</span>
                        )
                      ) : (
                        <span className='text-black/50'>—</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ItineraryTableView;
